import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, DISABLED_CONTROL, FONT_SIZE, GAP_TONE, GAPS_LIST, INK, MOTION, NEUTRAL, STATUS, SURFACE } from "../../../theme/tokens";
import { getStaffingStatusStyle } from "../WeeklyGapView/CountBadge";
import { formatStaffCount, type GapsMissingEntry } from "./gapsDayModel";
import { ROW_LEAD_WIDTH_PX } from "./gapsRowLayout";
import { useGapsCountsHidden } from "./useGapsCountsHidden";

type Props = {
	entry: GapsMissingEntry;
	/** The job the hole is in, so the row can name what is missing rather than say "1 short". */
	jobLabel: string;
	onAdd: (entry: GapsMissingEntry) => void;
	/**
	 * Nothing may be added under this section (`isSectionWriteBlocked`): the hole still shows, but its Add is off. The
	 * group says why, once, under its header.
	 */
	isAddBlocked: boolean;
};

/**
 * One missing person, named by the location that is short.
 *
 * This is the screen's central idea: "Cart B, one short" is a job, and "3/5" is arithmetic. Two holes
 * in one location are two rows, because a shortfall should read as a list of things to do rather than
 * a number to work out.
 *
 * A row marked "request open" is the same hole as a posted request standing against it — counted once
 * and drawn twice, deliberately, so the hole stays visible while the request is still unanswered.
 */
export const GapsMissingRow: FC<Props> = ({ entry, jobLabel, onAdd, isAddBlocked }) => {
	const { t } = useTranslation();
	const { areCountsHidden } = useGapsCountsHidden();
	// The artifact's `--crit-line`: the error ink as a hairline, flagged without shouting like a fill.
	const critLine = alpha(STATUS.ERROR.main, ALPHA.STATUS_BORDER);
	// The location's own count, coloured as the desktop's location header colours the same count.
	const countStyle = getStaffingStatusStyle(entry.filled, entry.required);

	return (
		<ButtonBase
			data-testid={`gaps-missing-row-${entry.locationName || "unassigned"}`}
			onClick={() => onAdd(entry)}
			disabled={isAddBlocked}
			sx={{
				width: "100%",
				display: "flex",
				alignItems: "center",
				gap: 1,
				pl: 1.25,
				pr: 1.125,
				py: 1.125,
				textAlign: "left",
				bgcolor: GAP_TONE.CRITICAL_BG,
				borderLeft: `${GAPS_LIST.EDGE_PX}px solid ${critLine}`,
				borderBottom: `1px solid ${NEUTRAL[150]}`,
				"@media (hover: hover) and (pointer: fine)": {
					"&:hover .gaps-missing-add": { bgcolor: STATUS.ERROR.main, color: SURFACE, borderColor: STATUS.ERROR.main },
				},
				"&.Mui-disabled .gaps-missing-add": { bgcolor: DISABLED_CONTROL.FILL, color: DISABLED_CONTROL.INK, borderColor: DISABLED_CONTROL.BORDER },
			}}
		>
			<Box
				component="span"
				sx={{
					flex: "none",
					width: ROW_LEAD_WIDTH_PX,
					height: 22,
					borderRadius: "3px",
					border: `1px dashed ${critLine}`,
					color: STATUS.ERROR.main,
					display: "grid",
					placeItems: "center",
				}}
			>
				<AddIcon sx={{ fontSize: 14 }} />
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography component="span" sx={{ display: "block", fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: STATUS.ERROR.main }}>
					{t("gaps.missing.title", { job: jobLabel.toLowerCase() })}
				</Typography>
				<Typography
					component="span"
					sx={{
						display: "block",
						fontSize: FONT_SIZE.LABEL_XS,
						color: INK.SECONDARY,
						mt: "1px",
						fontVariantNumeric: "tabular-nums",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					<Box component="span" sx={{ color: INK.PRIMARY, fontWeight: 600 }}>
						{entry.locationShortName || t("gaps.missing.noLocation")}
					</Box>
					{!areCountsHidden && (
						<>
							{" · "}
							{/* Filled is a WEIGHTED count — a half shift is 0.5 — so it goes through the screen's one formatter. */}
							<Box component="span" data-testid="gaps-missing-count" sx={{ color: countStyle.color, fontWeight: countStyle.fontWeight }}>
								{`${formatStaffCount(entry.filled)}/${formatStaffCount(entry.required)}`}
							</Box>
						</>
					)}
					{` · ${entry.timeLabel}`}
					{entry.hasOpenRequest && (
						<Box component="span" data-testid="gaps-missing-request-open" sx={{ color: INK.MUTED }}>
							{` · ${t("gaps.missing.requestOpen")}`}
						</Box>
					)}
				</Typography>
			</Box>
			<Box
				component="span"
				className="gaps-missing-add"
				sx={{
					flex: "none",
					border: `1px solid ${critLine}`,
					borderRadius: "7px",
					px: 1.125,
					py: 0.5,
					bgcolor: SURFACE,
					fontSize: FONT_SIZE.LABEL_SM,
					fontWeight: 700,
					color: STATUS.ERROR.main,
					transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
				}}
			>
				{t("gaps.missing.add")}
			</Box>
			<ChevronRightIcon sx={{ fontSize: 16, color: INK.MUTED, flex: "none" }} />
		</ButtonBase>
	);
};
